import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, NgModule, OnInit, ViewChild } from '@angular/core';
import {
  DxChartComponent,
  DxChartModule,
  DxDropDownButtonModule,
  DxPivotGridComponent,
  DxPivotGridModule,
  DxRadioGroupModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTreeViewModule
} from 'devextreme-angular';
import PivotGridDataSource, { Field, Options } from 'devextreme/ui/pivot_grid/data_source';
import { Item } from 'devextreme/ui/tree_view';
import { ReverseDimensionItem, ReverseDimensionMap, YearObject } from '../../interfaces';
import { MetaDataService } from '../../services';
import { LanguageService, PivotGridConfigService, UserService } from '../../services';
import { FilterYearOptions } from '../../utils/pivot-grid-config';
// import { Workbook} from 'devextreme-exceljs-fork';
import { saveAs } from 'file-saver';
// Our demo infrastructure requires us to use 'file-saver-es'. We recommend that you use the official 'file-saver' package in your applications.
import { Workbook } from 'exceljs';
import { exportPivotGrid } from 'devextreme/excel_exporter';
import { environment } from './../../../../environments/environment';

interface ChartType {
  name: string;
  value: string;
}

interface PublicType {
  name: string;
  value: boolean;
}

interface GroupValues {
  key: string;
  empty: boolean;
}

interface DistinctYearsItem {
  name: string;
  value: string;
  public: boolean;
}

@Component({
  selector: 'app-pivot-grid',
  templateUrl: './pivot-grid.component.html',
  styleUrls: ['./pivot-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PivotGridComponent implements OnInit, AfterViewInit {
  @ViewChild(DxPivotGridComponent, { static: false })
  pivotGrid!: DxPivotGridComponent;

  @ViewChild(DxChartComponent, { static: false })
  chart?: DxChartComponent;

  get report(): any {
    return this._report;
  }

  set report(id: any) {
    this._report = id;
  }

  get reportId(): string {
    return this._reportId;
  }

  set reportId(id: string) {
    this._reportId = id;
  }

  get parentDimensionLabels(): string[] {
    return this._parentDimensionLabels;
  }

  set parentDimensionLabels(labels: string[]) {
    this._parentDimensionLabels = labels;
  }

  get chartShow(): boolean {
    return !!this._showChart;
  }

  set chartShow(show: boolean) {
    this._showChart = show;
  }

  get chartInverted(): boolean {
    return !!this._invertChart;
  }

  set chartInverted(invert: boolean) {
    this._invertChart = invert;
  }

  get chartDefault(): string {
    return this._defaultChart;
  }

  set chartDefault(chType: string) {
    this._defaultChart = chType;
  }

  get treeHeaderLayout(): boolean {
    return !!this._treeHeaderLayout;
  }

  set treeHeaderLayout(tree: boolean) {
    this._treeHeaderLayout = (tree === undefined) ? true : tree;
  }

  set options(options: Options) {
    this._options = options;
  }

  set dimensions(dimensions: ReverseDimensionMap) {
    this._dimensions = dimensions;
  }

  set distinctYears(distinctYears: YearObject[]) {
    this.setDistinctYears(distinctYears);
  }

  set filterYear(filterYear: FilterYearOptions) {
    this._filterYear = filterYear;
  }

  get filterValues(): Item[] {
    return this.pivotGrid?.instance
      .getDataSource()
      .fields()
      ?.filter((field: any) => field.filterValues)
      .map((field: any) => this.createFilterObject(field));
  }

  // get filters4PrivotGrid() {
  //   return this.pivotGrid?.instance
  //     .getDataSource()
  //     .fields()
  //     ?.filter((field: any) => field.filterValues)
  // }

  chartTypes: ChartType[] = [
    {
      name: this.languageService.get('chartType_bar'),
      value: 'bar',
    },
    {
      name: this.languageService.get('chartType_line'),
      value: 'line',
    },
    {
      name: this.languageService.get('chartType_area'),
      value: 'area',
    },
    {
      name: this.languageService.get('chartType_fullstackedarea'),
      value: 'fullstackedarea',
    },
    {
      name: this.languageService.get('chartType_fullstackedbar'),
      value: 'fullstackedbar',
    },
    {
      name: this.languageService.get('chartType_fullstackedline'),
      value: 'fullstackedline',
    },
    {
      name: this.languageService.get('chartType_fullstackedspline'),
      value: 'fullstackedspline',
    },
    {
      name: this.languageService.get('chartType_fullstackedsplinearea'),
      value: 'fullstackedsplinearea',
    },
    {
      name: this.languageService.get('chartType_scatter'),
      value: 'scatter',
    },
    {
      name: this.languageService.get('chartType_spline'),
      value: 'spline',
    },
    {
      name: this.languageService.get('chartType_splinearea'),
      value: 'splinearea',
    },
    {
      name: this.languageService.get('chartType_stackedarea'),
      value: 'stackedarea',
    },
    {
      name: this.languageService.get('chartType_stackedbar'),
      value: 'stackedbar',
    },
    {
      name: this.languageService.get('chartType_stackedline'),
      value: 'stackedline',
    },
    {
      name: this.languageService.get('chartType_stackedspline'),
      value: 'stackedspline',
    },
    {
      name: this.languageService.get('chartType_stackedsplinearea'),
      value: 'stackedsplinearea',
    },
    {
      name: this.languageService.get('chartType_steparea'),
      value: 'steparea',
    },
    {
      name: this.languageService.get('chartType_stepline'),
      value: 'stepline',
    },
  ];

  publicTypes: PublicType[] = [
    {
      name: this.languageService.get('only_public'),
      value: true,
    },
    {
      name: this.languageService.get('all_public'),
      value: false,
    },
  ];

  pivotGridDataSource!: PivotGridDataSource;
  groupValues: GroupValues[] = [];
  distinctYearsItems!: DistinctYearsItem[];
  distinctYearValue: string[] = [];
  isLoggedIn!: boolean;

  public = true;

  isMysqlDriver: boolean;

  private _options!: Options;
  private _report?: any;
  private _reportId?: any;
  private _showChart?: boolean;
  private _invertChart = false;
  private _defaultChart = 'bar';
  private _dimensions: ReverseDimensionMap = new Map<string, ReverseDimensionItem>();
  private _distinctYears!: DistinctYearsItem[];
  private _filterYear?: FilterYearOptions;
  private _treeHeaderLayout?: boolean;
  private _parentDimensionLabels: string[] = [];

  constructor(
    private ref: ChangeDetectorRef,
    private languageService: LanguageService,
    private pivotGridConfigService: PivotGridConfigService,
    private userService: UserService,
    private metaDataService: MetaDataService
  ) {
    this.isMysqlDriver = environment.DB_DRIVER === "mysql" || false;
  }

  async ngOnInit(): Promise<void> {
    this.isLoggedIn = !!this.userService.getUser();

    if (!this.isMysqlDriver) this._distinctYears = this._distinctYears.reverse();

    this.distinctYearsItems = this.public ? this._distinctYears.filter((distinctYear) => distinctYear.public) : this._distinctYears;
    if (this._filterYear) {
      this.setDefaultDistinctYearValue();
    }

    this.pivotGridDataSource = new PivotGridDataSource({
      ...this._options,
      filter: this.createFilterValues(),
      store: {
        ...this._options.store,
        onLoaded: (result: any) => {
          //@ts-ignore
          result.forEach((item, ix, items) => {
            let isRemovalble = false;
            // console.debug("Item here: ", item)
            if (this._dimensions.get(item.dimension_1)) {
              item['dimensiondata_1'] = this._dimensions.get(item.dimension_1);

            } else {
              if (item.dimension_1 !== null) isRemovalble = true;
            }
            if (this._dimensions.get(item.dimension_2)) {
              item['dimensiondata_2'] = this._dimensions.get(item.dimension_2);
            } else {
              if (item.dimension_2 !== null) isRemovalble = true;
            }
            if (this._dimensions.get(item.dimension_3)) {
              item['dimensiondata_3'] = this._dimensions.get(item.dimension_3);
            } else {
              if (item.dimension_3 !== null) isRemovalble = true;
            }
            if (isRemovalble) {
              console.error(
                `Hibás tétel: ix: ${ix}, dimension_1: ${item.dimension_1}, dimension_2: ${item.dimension_2}, dimension_3: ${item.dimension_3}`
              );
              items.splice(ix, 1);
            }
          });
          // console.debug('Dimensions: ', this._dimensions)
        },
      },
    });

  }

  ngAfterViewInit(): void {
    if (this._showChart) {
      this.pivotGrid.instance.bindChart(this.chart?.instance, {
        dataFieldsDisplayMode: 'splitPanes',
        alternateDataFields: false,
        inverted: this.chartInverted,
        customizeChart: (chartOptions: any) => {
          const passed = chartOptions.panes.filter((item: any) => {
            return (!["Dimensiondata 1.ord (Min)", "Dimensiondata 2.ord (Min)"].includes(item.name));
          })
          chartOptions.panes = passed;
          return chartOptions;
        }
      });
    }
    // this.resetFilters4PrivotGrid(); //
  }

  filterPublic(e: boolean): void {
    this.pivotGridConfigService.getDistinctYears((this._options.store as any).url.split('/').pop()).subscribe((distinctYears) => {

      if (!this.isMysqlDriver) distinctYears.reverse()
      this.setDistinctYears(distinctYears);

      if (e) {
        this.distinctYearsItems = this._distinctYears.filter((distinctYear) => distinctYear.public);

        this.distinctYearValue = this.distinctYearValue.filter(
          (value) => this.distinctYearsItems.find((item) => item.value === value)?.public
        );

        if (!this.distinctYearValue.length) {
          this.setDefaultDistinctYearValue();
        }

        this.ref.markForCheck();
      } else {
        this.distinctYearsItems = this._distinctYears;
        this.filterPivotGrid();
      }
    });
  }

  filterPivotGrid(): void {
    this.pivotGridDataSource.filter(this.createFilterValues());
    this.pivotGridDataSource.reload();
  }

  onCellPrepared(e: any): void {
    // If there is no any hierarhical dimension then we should not do anything
    if (this.parentDimensionLabels.length === 0) return;

    // We intrested only the "row" or "column" areas with type "D" cell
    if (e.area === 'data' || !e.cell.path || e.cell.type === 'T') return;

    // Check the last leaf or undefined items of the path for area row and column
    if (this.hasHidenableDimensionItem(e)) {
      this.hideExpandArrow(e);
    }
  }

  onCellClick(e: any): void {
    e.cancel = (this.hasHidenableDimensionItem(e));
  }

  expandSelected(e: any): void {
    this.pivotGridDataSource.expandHeaderItem(
      'row',
      e.target.data.series
        .split(' | ')[0]
        .split(' - ')
        .map((serie: string) => serie)
    );
  }

  onExporting(e: any) {
    const date = new Date().toISOString().split('T').shift();

    // Create the workbook and load the logo image
    const workbook = new Workbook();
    const logo = workbook.addImage({
      base64: this.metaDataService.getBase64Logo(),
      extension: 'jpeg',
    });
    
    // Meta data sheet preparation
    const workSheetTitle = this.languageService.get('metaws_title');
    const metaSheet = workbook.addWorksheet(workSheetTitle);

    this.metaDataService.extractMetaDataHeader(this.report, metaSheet, logo);

    const filterYears = this.distinctYearValue;
    const fields = this.pivotGrid?.instance.getDataSource().fields()
    this.metaDataService.extractFilters(metaSheet, fields, filterYears, this.filterValues)
    
    // Report data sheet preparation
    const entityURL = (this._options.store as any).url;
    const reportId = entityURL.split('/').pop();


    const worksheet = workbook.addWorksheet(`${reportId}`);

    exportPivotGrid({
      component: e.component,
      worksheet,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer: any) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${reportId}_${date}.xlsx`);
      });
    });
  }

  private hasHidenableDimensionItem(e: any): boolean {
    // get the last index of the cell's path
    const { path } = e.cell;
    const lastPathItem = path.length - 1;

    // get the last index of the cell's fields
    const fields = e.component.getDataSource().getAreaFields(e.area);
    const lastFildItem = fields.length - 1

    // Check wether we are at the tail of the stack, if not do nothing.
    if (fields[lastPathItem].groupName && fields[lastPathItem].groupName !== fields[lastFildItem].groupName) return false;

    // Do nothing with date type fields
    const fieldDataType = fields.map((f: Field) => f.dataField);
    if (fieldDataType[lastPathItem] === 'date') return false;

    // Is there an undefined or a dimension leaf item at the last segment of the path?    
    if (path[lastPathItem] === undefined || !this.parentDimensionLabels.includes(path[lastPathItem])) {
      // Has  that item an "expand icon"?
      if (e.cellElement.querySelector('.dx-expand-icon-container')) return true;
    }
    return false;
  }

  private hideExpandArrow(e: any): void {
    const cell = e.cellElement.querySelector('.dx-expand-icon-container');

    if (cell) {
      Object.assign(e.cellElement.style, { cursor: 'initial' });
      console.debug('cell.innerHTML: ', cell.innerHTML)
      cell.innerHTML = '';
    }
  }

  private resetFilters4PrivotGrid() {
    const fields = this.pivotGrid?.instance
      .getDataSource()
      .fields()
      ?.filter((field: any) => field.filterValues)
    fields.map(field => {
      if (field.area !== 'filter') field.filterValues = undefined;
      return field;
    });
  }

  private createItems(filterValue: any, items: Item[] = [], parentId?: string): Item[] {
    if (Array.isArray(filterValue) && filterValue.length) {
      const id = filterValue.shift();

      items.push({ id, text: id, ...(parentId && { parentId }) });

      if (filterValue.length) {
        this.createItems(filterValue, items, id);
      }
    } else {
      items.push({ id: filterValue, text: filterValue });
    }
    return items;
  }

  private createFilterValues(): any {
    let filterValues: any = [];

    if (this.distinctYearValue.length) {
      this.distinctYearValue.forEach((year) => {
        if (filterValues.length) {
          filterValues.push('or');
        }
        // filterValues.push(['yr', year]);
        filterValues.push((this.isMysqlDriver) ? ['yr', year] : ['year(date)', year]);
      });
    } else if (!this.public) {
      filterValues = null;
    }

    if (this.public) {
      if (filterValues.length) {
        filterValues = [filterValues];
      }

      filterValues.push(['public', this.public]);
    }

    // @TODO Remove
    console.debug("[PrivotGridCompont] created filter: ", filterValues)

    return filterValues;
  }

  private createFilterObject(field: Field): Item {
    const filterValues = field.filterValues?.map((filterValue) => this.createItems(JSON.parse(JSON.stringify(filterValue)))).flat();
    const filter: Item = { text: field.caption, items: [...new Map(filterValues?.map((v) => [v.id, v])).values()] };

    if (field.name === 'month') {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      console.debug('Month filter item: ', filter.items)
      filter.items = filter.items!.map((month: any) => {
        return { text: new Intl.DateTimeFormat(this.languageService.language, { month: 'long' }).format(new Date().setMonth(month.id - 1)) };
      });
    }
    return filter;
  }

  private setDefaultDistinctYearValue(): void {
    this.distinctYearValue = this.distinctYearsItems.slice(0, this._filterYear?.numberOfYears ?? 1).map((item) => item.value);
  }

  // @TODO Need to review 
  private setDistinctYears(distinctYears: YearObject[]): void {
    this._distinctYears = distinctYears.map(
      (distinctYear) =>
      ({
        name: distinctYear.yr,
        value: distinctYear.yr,
        public: distinctYear.public,
      } as DistinctYearsItem)
    );
  }

}

@NgModule({
  imports: [
    CommonModule,
    DxChartModule,
    DxDropDownButtonModule,
    DxPivotGridModule,
    DxRadioGroupModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTreeViewModule,
  ],
  declarations: [PivotGridComponent],
  exports: [PivotGridComponent],
})
export class PivotGridModule { }
